// LOOTUS — editor de "Descrição / Histórico do serviço" (Ficha de Serviço).
// Um único editor Tiptap (useEditor) compartilhado entre o campo compacto
// e o modal expandido — nunca duas instâncias/dois estados a sincronizar
// (item 3 do pedido: "não criar duas versões independentes"). O modal
// expandido só move ONDE o <EditorContent> é renderizado; o conteúdo/
// histórico de undo/cursor são do mesmo `editor`, preservados
// automaticamente.
import { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, AlignLeft, AlignCenter, AlignRight,
  Undo2, Redo2, RemoveFormatting, Quote, Minus, Maximize2,
} from "lucide-react";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import {
  buildRichTextExtensions, normalizeEditorContent, applyTextStyle, getActiveTextStyle,
  FONT_FAMILY_OPTIONS, TEXT_STYLE_OPTIONS,
} from "../lib/richText";

function ToolbarButton({ active, onClick, title, children, testId }) {
  return (
    <button
      type="button"
      title={title}
      data-testid={testId}
      onMouseDown={(e) => e.preventDefault()} // não tira o foco do editor ao clicar
      onClick={onClick}
      className={`h-8 w-8 inline-flex items-center justify-center rounded-md transition-colors ${
        active ? "bg-primary/15 text-primary" : "text-slate-600 hover:bg-slate-100"
      }`}
    >
      {children}
    </button>
  );
}

function RichTextToolbar({ editor, testIdPrefix }) {
  if (!editor) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 p-1.5 border-b border-slate-200 bg-slate-50 rounded-t-md">
      <Select value={getActiveTextStyle(editor)} onValueChange={(v) => applyTextStyle(editor, v)}>
        <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`${testIdPrefix}-estilo`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {TEXT_STYLE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select
        value={editor.getAttributes("textStyle").fontFamily || "default"}
        onValueChange={(v) => (v === "default" ? editor.chain().focus().unsetFontFamily().run() : editor.chain().focus().setFontFamily(v).run())}
      >
        <SelectTrigger className="h-8 w-[150px] text-xs" data-testid={`${testIdPrefix}-fonte`}><SelectValue /></SelectTrigger>
        <SelectContent>
          {FONT_FAMILY_OPTIONS.map((o) => <SelectItem key={o.value || "default"} value={o.value || "default"}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <div className="w-px h-5 bg-slate-300 mx-0.5" />
      <ToolbarButton title="Negrito" active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} testId={`${testIdPrefix}-bold`}>
        <Bold size={15} />
      </ToolbarButton>
      <ToolbarButton title="Itálico" active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} testId={`${testIdPrefix}-italic`}>
        <Italic size={15} />
      </ToolbarButton>
      <ToolbarButton title="Sublinhado" active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} testId={`${testIdPrefix}-underline`}>
        <UnderlineIcon size={15} />
      </ToolbarButton>
      <div className="w-px h-5 bg-slate-300 mx-0.5" />
      <ToolbarButton title="Lista com marcadores" active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} testId={`${testIdPrefix}-bullet-list`}>
        <List size={15} />
      </ToolbarButton>
      <ToolbarButton title="Lista numerada" active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} testId={`${testIdPrefix}-ordered-list`}>
        <ListOrdered size={15} />
      </ToolbarButton>
      <ToolbarButton title="Citação" active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} testId={`${testIdPrefix}-quote`}>
        <Quote size={15} />
      </ToolbarButton>
      <ToolbarButton title="Linha separadora" onClick={() => editor.chain().focus().setHorizontalRule().run()} testId={`${testIdPrefix}-hr`}>
        <Minus size={15} />
      </ToolbarButton>
      <div className="w-px h-5 bg-slate-300 mx-0.5" />
      <ToolbarButton title="Alinhar à esquerda" active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()} testId={`${testIdPrefix}-align-left`}>
        <AlignLeft size={15} />
      </ToolbarButton>
      <ToolbarButton title="Centralizar" active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()} testId={`${testIdPrefix}-align-center`}>
        <AlignCenter size={15} />
      </ToolbarButton>
      <ToolbarButton title="Alinhar à direita" active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()} testId={`${testIdPrefix}-align-right`}>
        <AlignRight size={15} />
      </ToolbarButton>
      <div className="w-px h-5 bg-slate-300 mx-0.5" />
      <ToolbarButton title="Desfazer" onClick={() => editor.chain().focus().undo().run()} testId={`${testIdPrefix}-undo`}>
        <Undo2 size={15} />
      </ToolbarButton>
      <ToolbarButton title="Refazer" onClick={() => editor.chain().focus().redo().run()} testId={`${testIdPrefix}-redo`}>
        <Redo2 size={15} />
      </ToolbarButton>
      <ToolbarButton title="Limpar formatação" onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} testId={`${testIdPrefix}-clear`}>
        <RemoveFormatting size={15} />
      </ToolbarButton>
    </div>
  );
}

export default function RichTextEditorField({ value, onChange, testId = "form-descricao", placeholder = "Descreva o atendimento..." }) {
  const [expanded, setExpanded] = useState(false);
  const lastEmitted = useRef(value);
  const extensions = useRef(buildRichTextExtensions(placeholder)).current;

  const editor = useEditor({
    extensions,
    content: normalizeEditorContent(value),
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      lastEmitted.current = html;
      onChange(html);
    },
  });

  // Sincroniza quando `value` muda por causa EXTERNA (ex.: abrir a mesma
  // instância de formulário para outra ficha, sem desmontar o componente)
  // — nunca quando a mudança veio do próprio editor (onUpdate acima).
  useEffect(() => {
    if (!editor) return;
    if (value === lastEmitted.current) return;
    editor.commands.setContent(normalizeEditorContent(value), false);
    lastEmitted.current = value;
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div>
      <div className="relative border border-slate-200 rounded-md">
        <button
          type="button"
          title="Expandir editor"
          data-testid={`${testId}-expandir`}
          onClick={() => setExpanded(true)}
          className="absolute top-1.5 right-1.5 z-10 h-6 w-6 inline-flex items-center justify-center rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100"
        >
          <Maximize2 size={13} />
        </button>
        {!expanded && (
          <div className="h-28 overflow-y-auto px-2.5 py-2 pr-8 cursor-text" data-testid={testId} onClick={() => editor.chain().focus().run()}>
            <EditorContent editor={editor} />
          </div>
        )}
      </div>

      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="max-w-[90vw] w-[90vw] h-[85vh] flex flex-col p-4">
          <DialogHeader><DialogTitle>Descrição / Histórico do serviço</DialogTitle></DialogHeader>
          <div className="flex-1 min-h-0 flex flex-col border border-slate-200 rounded-md overflow-hidden">
            <RichTextToolbar editor={editor} testIdPrefix={`${testId}-toolbar`} />
            <div className="flex-1 overflow-y-auto px-4 py-3 cursor-text" onClick={() => editor.chain().focus().run()}>
              {expanded && <EditorContent editor={editor} />}
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setExpanded(false)} className="bg-primary hover:bg-primary/90 text-primary-foreground" data-testid={`${testId}-concluir`}>
              Concluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
